import React from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { logUiError } from "../services/uiLogs.service";

const StyledGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "name",
})({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
  flexDirection: "column",
  rowGap: "0.5rem",
  "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
  "& .MuiTypography-root": { fontSize: "0.88rem", textAlign: "center" },

  variants: [
    {
      props: { name: "footer" },
      style: { rowGap: 0 },
    },
    {
      props: { name: "leftNav" },
      style: { marginTop: "55%" },
    },
  ],
});

function AppErrorFallback({ errorCode, componentName }) {
  return (
    <StyledGrid className="errorFallbackContainer" name={componentName}>
      <InfoOutlinedIcon />
      <Typography tabIndex={0}>
        We apologize for the inconvenience. This section of the page is
        currently experiencing technical difficulties. Please try again later or
        contact our support team for further assistance with this error code{" "}
        {errorCode}. Thank you for your patience.
      </Typography>
    </StyledGrid>
  );
}

function ErrorBoundaryWrapper({ name, children }) {
  const uniqueId = React.useRef(Date.now());
  const onError = (error, info) => {
    const data = {
      message: error?.message,
      stackTrace: info?.componentStack
        ? info.componentStack
            .split("\n")
            .filter(Boolean)
            .map((line) => line.trim())
            .slice(0, 9)
        : "",
    };
    logUiError(data, "runtime", uniqueId?.current);
  };

  return (
    <ErrorBoundary
      fallback={
        <AppErrorFallback componentName={name} errorCode={uniqueId?.current} />
      }
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  );
}
export default ErrorBoundaryWrapper;

export const useErrorHandling = () => {
  const { showBoundary } = useErrorBoundary();

  function withErrorHandling(handler) {
    return (...args) => {
      try {
        return handler(...args);
      } catch (e) {
        showBoundary(e);
      }
      return undefined;
    };
  }
  return withErrorHandling;
};
